import React, { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Loader from "../../components/common/Loader";
import generateQuotationPDF from "../../pages/Quotation/generateQuotationPDF";
import { styles } from "../../styles/pages/Quotation/QuotationViewStyles";

// Interfaces from QuotesTable
interface Quotation {
  id: string | number;
  quotation_number: string;
  group_total: string;
  quotation_type: string;
  quotation_status: string;
  opportunity: string;
  valid_until: string;
  log: string;
  quotation_subject?: string;
  from_company_name?: string;
  from_address?: string;
  from_phone?: string;
  from_email?: string;
  to_client_name?: string;
  to_address?: string;
  to_email?: string;
  terms_and_conditions?: string;
}

interface ProductDetails {
  id: number;
  name: string;
  description: string;
  price: string;
  quantity: number;
  CGST?: string;
  SGST?: string;
  IGST?: string;
  discount?: string;
  discountType?: number;
  type?: string;
  status?: number;
}

interface ServiceDetails {
  id: number;
  title: string;
  description: string;
  duration: string;
  cost: string;
  service_GST?: string;
  discount?: string;
  discountType?: number;
  type?: string;
}

const QuotationView: React.FC = () => {
  const location = useLocation();
  const { quotationNumber } = location.state || {};
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [viewDetails, setViewDetails] = useState<(ProductDetails | ServiceDetails)[]>([]);
  const [viewType, setViewType] = useState<"Product" | "Service" | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [recalculatedGroupTotal, setRecalculatedGroupTotal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isEmailSuccessModalOpen, setEmailSuccessModalOpen] = useState(false);

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("No auth token found.");
      return null;
    }
    return token;
  };

  const axiosConfig = (token: string) => ({
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  });

  const fetchDetails = useCallback(async () => {
    if (!quotationNumber) {
      setError("Quotation number not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    const token = getAuthToken();
    if (!token) {
      setError("Authentication token not found.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotation/search/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200 && Array.isArray(response.data.data)) {
        const data = response.data.data.find(
          (item: any) =>
            String(item.quotation_number).trim() === String(quotationNumber).trim()
        );

        if (data) {
          const selectedData: Quotation = {
            id: data.id,
            quotation_number: data.quotation_number,
            group_total: data.group_total,
            quotation_type: data.quotation_type,
            quotation_status: data.quotation_status,
            opportunity: data.opportunity,
            valid_until: data.valid_until,
            log: data.log || "",
            quotation_subject: data.quotation_subject || "",
            from_company_name: data.from_company_name || "N/A",
            from_address: data.from_address || "N/A",
            from_phone: data.from_phone || "N/A",
            from_email: data.from_email || "N/A",
            to_client_name: data.to_client_name || "N/A",
            to_address: data.to_address || "N/A",
            to_email: data.to_email || "N/A",
            terms_and_conditions: data.terms_and_conditions || "N/A",
          };

          const isProduct: boolean =
            data.quotation_type === "Product" ||
            data.quotation_type === 1 ||
            data.quotation_type === "1";

          let details: (ProductDetails | ServiceDetails)[] = [];
          let recalculatedTotal: number = 0;

          if (isProduct && Array.isArray(data.products)) {
            details = data.products
              .filter((product: any) => product.status !== 0)
              .map((product: any) => {
                const CGST = parseFloat(product.CGST || "0");
                const SGST = parseFloat(product.SGST || "0");
                const IGST = parseFloat(product.IGST || "0");
                const price = parseFloat(product.price || "0");
                const quantity = parseInt(product.quantity || "1", 10);
                const discountStr = String(product.discount || "0").trim();
                const discountType = product.discount_type || 1;
                const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

                // Calculate product total for recalculation
                const subtotal = price * quantity;
                let discount = discountType === 1 ? subtotal * (discountValue / 100) : discountValue;
                const taxableAmount = subtotal - discount;
                const cgstAmount = taxableAmount * (CGST / 100);
                const sgstAmount = taxableAmount * (SGST / 100);
                const igstAmount = taxableAmount * (IGST / 100);
                const totalGSTAmount = IGST > 0 ? igstAmount : cgstAmount + sgstAmount;
                const total = taxableAmount + totalGSTAmount;

                recalculatedTotal += total;

                return {
                  id: product.id || 0,
                  name: product.product_name || product.name || "Unknown Product",
                  description: product.description || "No description available",
                  price: String(product.price || "0"),
                  quantity,
                  CGST: String(CGST),
                  SGST: String(SGST),
                  IGST: String(IGST),
                  discount: discountStr,
                  discountType,
                  type: product.type || "Standard",
                  status: product.status || 1,
                };
              });
          } else if (!isProduct && Array.isArray(data.services)) {
            details = data.services.map((service: any) => {
              const price = parseFloat(service.cost || service.price || "0");
              const discountStr = String(service.discount || "0").trim();
              const discountType = service.discount_type || 1;
              const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;
              const gstRate = parseFloat(service.service_GST || "0");

              // Calculate service total for recalculation
              const subtotal = price;
              let discount = discountType === 1 ? subtotal * (discountValue / 100) : discountValue;
              const taxableAmount = subtotal - discount;
              const gstAmount = taxableAmount * (gstRate / 100);
              const total = taxableAmount + gstAmount;

              recalculatedTotal += total;

              return {
                id: service.id || 0,
                title: service.service_name || service.title || "Unknown Service",
                description: service.description || "No description available",
                duration: service.duration || "N/A",
                cost: String(service.cost || service.price || "0"),
                service_GST: String(service.service_GST || "0"),
                discount: discountStr,
                discountType,
                type: service.type || "Standard",
              };
            });
          } else {
            console.warn("No product or service data found for this quotation.");
            details = [];
          }

          setQuotation(selectedData);
          setViewType(isProduct ? "Product" : "Service");
          setViewDetails(details);
          setRecalculatedGroupTotal(recalculatedTotal.toFixed(2)); // Store recalculated total
        } else {
          setError("No details found for this quotation.");
          setViewDetails([]);
        }
      } else {
        setError("No valid data found for this quotation.");
        setViewDetails([]);
      }
    } catch (err) {
      console.error("Details fetch Error:", err);
      setError(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to fetch quotation details."
          : "An unexpected error occurred."
      );
      setViewDetails([]);
    } finally {
      setLoading(false);
    }
  }, [quotationNumber]);

  const isDateExpired = (validUntil: string): boolean => {
    const [day, month, year] = validUntil.split("-").map(Number);
    const validDate = new Date(year, month - 1, day);
    const today = new Date();

    if (isNaN(validDate.getTime())) {
      console.error(`Invalid date format for validUntil: ${validUntil}`);
      return false;
    }

    validDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return validDate < today;
  };

  const handleDownloadPDF = async () => {
    if (!quotation || !viewType) {
      alert("No quotation data available for PDF generation.");
      return;
    }

    setIsDownloadLoading(true);

    const formattedQuotation = {
      quotation_number: quotation.quotation_number,
      quotation_subject: quotation.quotation_subject || "N/A",
      quotation_type: quotation.quotation_type || "N/A",
      valid_until: quotation.valid_until || "N/A",
      group_total: recalculatedGroupTotal || quotation.group_total || "N/A",
      // group_total: quotation.group_total || "N/A",
      from_company_name: quotation.from_company_name || "N/A",
      from_address: quotation.from_address || "N/A",
      from_phone: quotation.from_phone || "N/A",
      from_email: quotation.from_email || "N/A",
      to_client_name: quotation.to_client_name || "N/A",
      to_address: quotation.to_address || "N/A",
      to_email: quotation.to_email || "N/A",
      terms_and_conditions: quotation.terms_and_conditions || "N/A",
    };

    try {
      const pdfBlob = await generateQuotationPDF({
        quotation: formattedQuotation,
        details: viewDetails,
        viewType,
      });

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Quotation_${quotation.quotation_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!quotation || !viewType) {
      alert("No quotation data available for email.");
      return;
    }

    setIsEmailLoading(true);

    try {
      const formattedQuotation = {
        quotation_number: quotation.quotation_number,
        quotation_subject: quotation.quotation_subject || "N/A",
        quotation_type: quotation.quotation_type || "N/A",
        valid_until: quotation.valid_until || "N/A",
        group_total: recalculatedGroupTotal || quotation.group_total || "N/A",
        from_company_name: quotation.from_company_name || "N/A",
        from_address: quotation.from_address || "N/A",
        from_phone: quotation.from_phone || "N/A",
        from_email: quotation.from_email || "N/A",
        to_client_name: quotation.to_client_name || "N/A",
        to_address: quotation.to_address || "N/A",
        to_email: quotation.to_email || "N/A",
        terms_and_conditions: quotation.terms_and_conditions || "N/A",
      };

      const pdfBlob = await generateQuotationPDF({
        quotation: formattedQuotation,
        details: viewDetails,
        viewType,
      });

      const token = getAuthToken();
      if (!token) {
        alert("Authentication token not found.");
        setIsEmailLoading(false);
        return;
      }

      const pdfFile = new File(
        [pdfBlob],
        `Quotation_${quotation.quotation_number}.pdf`,
        {
          type: "application/pdf",
        }
      );

      const formData = new FormData();
      formData.append("pdf", pdfFile);
      formData.append("email", formattedQuotation.to_email);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/send-quotation`,
        formData,
        axiosConfig(token)
      );

      if (response.status === 200) {
        setEmailSuccessModalOpen(true);
      } else {
        alert("Failed to send quotation email.");
      }
    } catch (error) {
      console.error("Email Send Error:", error);
      alert("An error occurred while sending the email. Please try again.");
    } finally {
      setIsEmailLoading(false);
    }
  };

  const isProductDetails = (
    item: ProductDetails | ServiceDetails
  ): item is ProductDetails => {
    return (
      (item as ProductDetails).price !== undefined &&
      (item as ProductDetails).quantity !== undefined
    );
  };

  const isServiceDetails = (
    item: ProductDetails | ServiceDetails
  ): item is ServiceDetails => {
    return (
      (item as ServiceDetails).cost !== undefined &&
      (item as ServiceDetails).title !== undefined
    );
  };

  const renderProductTable = () => {
    const productDetails = viewDetails.filter(isProductDetails);

    const calculateProductTotals = (product: ProductDetails) => {
      const price = parseFloat(product.price);
      const qty = product.quantity || 1;
      const subtotal = price * qty;

      const discountStr = (product.discount || "0").trim();
      const discountType = discountStr.endsWith("%") ? 1 : 2;
      const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

      let discount = 0;
      if (discountType === 1) {
        discount = subtotal * (discountValue / 100);
      } else {
        discount = discountValue;
      }

      const taxableAmount = subtotal - discount;
      const sgstRate = parseFloat(product.SGST || "0");
      const cgstRate = parseFloat(product.CGST || "0");
      const igstRate = parseFloat(product.IGST || "0");
      const cgstAmount = taxableAmount * (cgstRate / 100);
      const sgstAmount = taxableAmount * (sgstRate / 100);
      const igstAmount = taxableAmount * (igstRate / 100);
      const totalGSTAmount = igstRate > 0 ? igstAmount : cgstAmount + sgstAmount;
      const total = taxableAmount + totalGSTAmount;

      return {
        subtotal,
        discount,
        taxableAmount,
        cgstAmount,
        sgstAmount,
        igstAmount,
        totalGSTAmount,
        total,
        discountType,
        discountValue,
        sgstRate,
        cgstRate,
        igstRate,
      };
    };

    const aggregatedTotals = productDetails.map(calculateProductTotals);
    const totalPriceQty = aggregatedTotals.reduce((sum, p) => sum + p.subtotal, 0);
    const totalDiscount = aggregatedTotals.reduce((sum, p) => sum + p.discount, 0);
    const totalTaxable = totalPriceQty - totalDiscount;
    const totalSGST = aggregatedTotals.reduce((sum, p) => sum + p.sgstAmount, 0);
    const totalCGST = aggregatedTotals.reduce((sum, p) => sum + p.cgstAmount, 0);
    const totalIGST = aggregatedTotals.reduce((sum, p) => sum + p.igstAmount, 0);
    const totalGST = totalIGST > 0 ? totalIGST : totalCGST + totalSGST;
    const finalGroupTotal = totalTaxable + totalGST;

    const hasIGST = productDetails.some((product) => parseFloat(product.IGST || "0") > 0);

    return (
      <div className={styles.tableContainer}>
        <h4 className={styles.tableTitle}>Products</h4>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr>
                <th className={styles.tableHeadCell}>Sr No.</th>
                <th className={styles.tableHeadCell}>Product Name</th>
                <th className={styles.tableHeadCell}>Price</th>
                <th className={styles.tableHeadCell}>Qty</th>
                <th className={styles.tableHeadCell}>Discount</th>
                {hasIGST ? (
                  <th className={styles.tableHeadCell}>IGST</th>
                ) : (
                  <>
                    <th className={styles.tableHeadCell}>SGST</th>
                    <th className={styles.tableHeadCell}>CGST</th>
                  </>
                )}
                <th className={styles.tableHeadCell}>Total</th>
              </tr>
            </thead>
            <tbody>
              {productDetails.length > 0 ? (
                productDetails.map((product, index) => {
                  const {
                    total,
                    discountType,
                    discountValue,
                    cgstRate,
                    sgstRate,
                    igstRate,
                  } = calculateProductTotals(product);
                  const discountDisplay =
                    discountType === 1
                      ? `${discountValue.toFixed(2)}%`
                      : `${discountValue.toFixed(2)}₹`;
                  const hasDescription =
                    product.description &&
                    product.description !== "No description available";

                  return (
                    <>
                      <tr
                        key={`product-${product.id}-${index}`}
                        className={styles.tableRow}
                      >
                        <td className={styles.tableCell}>{index + 1}</td>
                        <td className={styles.tableCell}>{product.name}</td>
                        <td className={styles.tableCell}>
                          ₹
                          {parseFloat(product.price).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className={styles.tableCell}>{product.quantity}</td>
                        <td className={styles.tableCell}>{discountDisplay}</td>
                        {hasIGST ? (
                          <td className={styles.tableCell}>{igstRate.toFixed(2)}%</td>
                        ) : (
                          <>
                            <td className={styles.tableCell}>{sgstRate.toFixed(2)}%</td>
                            <td className={styles.tableCell}>{cgstRate.toFixed(2)}%</td>
                          </>
                        )}
                        <td className={styles.tableCell}>
                          ₹
                          {total.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                      {hasDescription && (
                        <tr
                          key={`product-desc-${product.id}-${index}`}
                          className={styles.descriptionRow}
                        >
                          <td
                            colSpan={hasIGST ? 6 : 8}
                            className={styles.descriptionCell}
                          >
                            Description:
                            <span className={styles.descriptionText}>
                              {" "}
                              {product.description}
                            </span>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={hasIGST ? 6 : 8}
                    className={styles.noDataCell}
                  >
                    No products available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.totalsContainer}>
          <p className={styles.totalText}>
            Subtotal:{" "}
            <span className={styles.totalValue}>
              ₹
              {Number(totalPriceQty).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </p>
          <p className={styles.totalText}>
            Discount:{" "}
            <span className={styles.totalValue}>
              ₹
              {Number(totalDiscount).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </p>
          {hasIGST ? (
            <p className={styles.totalText}>
              IGST:{" "}
              <span className={styles.totalValue}>
                ₹
                {Number(totalIGST).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </p>
          ) : (
            <>
              <p className={styles.totalText}>
                SGST:{" "}
                <span className={styles.totalValue}>
                  ₹
                  {Number(totalSGST).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </p>
              <p className={styles.totalText}>
                CGST:{" "}
                <span className={styles.totalValue}>
                  ₹
                  {Number(totalCGST).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </p>
            </>
          )}
          <p className={styles.groupTotal}>
            Group Total: ₹
            {Number(finalGroupTotal).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
      </div>
    );
  };

  const renderServiceTable = () => {
    const serviceDetails = viewDetails.filter(isServiceDetails);

    const calculateServiceTotals = (service: ServiceDetails) => {
      const price = parseFloat(service.cost);
      const subtotal = price;

      const discountStr = (service.discount || "0").trim();
      const discountType = discountStr.endsWith("%") ? 1 : 2;
      const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

      let discount = 0;
      if (discountType === 1) {
        discount = subtotal * (discountValue / 100);
      } else {
        discount = discountValue;
      }

      const taxableAmount = subtotal - discount;
      const gstRate = parseFloat(service.service_GST || "0");
      const gstAmount = taxableAmount * (gstRate / 100);
      const total = taxableAmount + gstAmount;

      return {
        subtotal,
        discount,
        taxableAmount,
        gstAmount,
        total,
        discountType,
        discountValue,
        gstRate,
      };
    };

    const aggregatedTotals = serviceDetails.map(calculateServiceTotals);
    const totalPrice = aggregatedTotals.reduce((sum, s) => sum + s.subtotal, 0);
    const totalDiscount = aggregatedTotals.reduce((sum, s) => sum + s.discount, 0);
    const totalTaxable = totalPrice - totalDiscount;
    const totalGST = aggregatedTotals.reduce((sum, s) => sum + s.gstAmount, 0);
    const finalGroupTotal = totalTaxable + totalGST;

    return (
      <div className={styles.tableContainer}>
        <h4 className={styles.tableTitle}>Services</h4>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr>
                <th className={styles.tableHeadCell}>Sr No.</th>
                <th className={styles.tableHeadCell}>Service Name</th>
                <th className={styles.tableHeadCell}>Price</th>
                <th className={styles.tableHeadCell}>Discount</th>
                <th className={styles.tableHeadCell}>GST</th>
                <th className={styles.tableHeadCell}>Total</th>
              </tr>
            </thead>
            <tbody>
              {serviceDetails.length > 0 ? (
                serviceDetails.map((service, index) => {
                  const { total, discountType, discountValue, gstRate } =
                    calculateServiceTotals(service);
                  const discountDisplay =
                    discountType === 1
                      ? `${discountValue.toFixed(2)}%`
                      : `${discountValue.toFixed(2)}₹`;
                  const hasDescription =
                    service.description &&
                    service.description !== "No description available";

                  return (
                    <>
                      <tr
                        key={`service-${service.id}-${index}`}
                        className={styles.tableRow}
                      >
                        <td className={styles.tableCell}>{index + 1}</td>
                        <td className={styles.tableCell}>{service.title}</td>
                        <td className={styles.tableCell}>
                          ₹
                          {parseFloat(service.cost).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className={styles.tableCell}>{discountDisplay}</td>
                        <td className={styles.tableCell}>{gstRate.toFixed(2)}%</td>
                        <td className={styles.tableCell}>
                          ₹
                          {total.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                      {hasDescription && (
                        <tr
                          key={`service-desc-${service.id}-${index}`}
                          className={styles.descriptionRow}
                        >
                          <td
                            colSpan={7}
                            className={styles.descriptionCell}
                          >
                            <span className={styles.descriptionText}>
                              Description: {service.description}
                            </span>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className={styles.noDataCell}
                  >
                    No services available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.totalsContainer}>
          <p className={styles.totalText}>
            Subtotal: ₹
            <span className={styles.totalValue}>
              {Number(totalPrice).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </p>
          <p className={styles.totalText}>
            Discount: ₹
            <span className={styles.totalValue}>
              {Number(totalDiscount).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </p>
          <p className={styles.totalText}>
            GST: ₹
            <span className={styles.totalValue}>
              {Number(totalGST).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </p>
          <p className={styles.groupTotal}>
            Group Total: ₹
            {Number(finalGroupTotal).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  if (loading) return <Loader />;
  if (error) return <p className="text-center text-red-500">{error}</p>;
  if (!quotation)
    return (
      <p className="text-center text-gray-500">
        No quotation data available.
      </p>
    );

  return (
    <div className={styles.container}>
      <link
        href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
        rel="stylesheet"
      />
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            Quotation Details for {quotation.to_client_name || "N/A"}
          </h3>
          <div className={styles.buttonGroup}>
            <button
              onClick={handleDownloadPDF}
              className={styles.downloadButton}
              disabled={isDownloadLoading}
              aria-label={
                isDownloadLoading ? "Generating PDF, please wait" : "Download PDF"
              }
            >
              {isDownloadLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
                  />
                </svg>
              ) : (
                <FontAwesomeIcon
                  icon={faDownload}
                  className="text-white text-sm"
                />
              )}
            </button>
            <button
              onClick={handleSendEmail}
              className={styles.emailButton}
              disabled={isEmailLoading}
              aria-label={
                isEmailLoading ? "Sending email, please wait" : "Send Email"
              }
            >
              {isEmailLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
                  />
                </svg>
              ) : (
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="text-white text-sm"
                />
              )}
            </button>
            <Link
              to="/quotation-list"
              className={styles.backButton}
            >
              <ArrowLeft className="inline-block mr-1" size={16} />
              Back
            </Link>
          </div>
        </div>
        <div className={styles.detailsContainer}>
          <div className={styles.section}>
            <div className={styles.fromToGrid}>
              <div>
                <h4 className={styles.fromToTitle}>From</h4>
                <p className={styles.infoText}>
                  Company Name :{" "}
                  <span className={styles.fromToValue}>
                    {quotation.from_company_name || "N/A"}
                  </span>
                </p>
                <p className={styles.infoText}>
                  Email :{" "}
                  <span className={styles.fromToValue}>
                    {quotation.from_email || "N/A"}
                  </span>
                </p>
                <p className={styles.infoText}>
                  Mobile Number :{" "}
                  <span className={styles.fromToValue}>
                    {quotation.from_phone || "N/A"}
                  </span>
                </p>
                <p className={styles.infoText}>
                  Address :{" "}
                  <span className={styles.fromToValue}>
                    {quotation.from_address || "N/A"}
                  </span>
                </p>
              </div>
              <div>
                <h4 className={styles.fromToTitle}>To</h4>
                <p className={styles.infoText}>
                  Client Name :{" "}
                  <span className={styles.fromToValue}>
                    {quotation.to_client_name || "N/A"}
                  </span>
                </p>
                <p className={styles.infoText}>
                  Email :{" "}
                  <span className={styles.fromToValue}>
                    {quotation.to_email || "N/A"}
                  </span>
                </p>
                <p className={styles.infoText}>
                  Address :{" "}
                  <span className={styles.fromToValue}>
                    {quotation.to_address || "N/A"}
                  </span>
                </p>
              </div>
            </div>
            <div className={styles.infoGrid}>
              <div>
                <p className={styles.infoText}>Quotation Number</p>
                <p className={styles.infoValue}>{quotation.quotation_number}</p>
              </div>
              <div>
                <p className={styles.infoText}>Quotation Type</p>
                <p className={styles.infoValue}>{quotation.quotation_type}</p>
              </div>
              <div>
                <p className={styles.infoText}>Quotation Subject</p>
                <p className={styles.infoValue}>
                  {quotation.quotation_subject || "N/A"}
                </p>
              </div>
              <div>
                <p className={styles.infoText}>Quotation Status</p>
                <p className={styles.infoValue}>{quotation.quotation_status}</p>
              </div>
              <div>
                <p className={styles.infoText}>Opportunity</p>
                <p className={styles.infoValue}>{quotation.opportunity}</p>
              </div>
              <div>
                <p className={styles.infoText}>Valid Till</p>
                <p className={styles.infoValue}>
                  {quotation.valid_until}
                  {isDateExpired(quotation.valid_until) && (
                    <span className="italic text-red-600 ml-1">
                      (Expired)
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div>
              <p className={styles.termsText}>Terms and Conditions</p>
              <p className={styles.termsValue}>
                {quotation.terms_and_conditions || "N/A"}
              </p>
            </div>
            {viewType === "Product"
              ? renderProductTable()
              : renderServiceTable()}
          </div>
        </div>
      </div>

      <div
        className={`${styles.modalOverlay} ${isEmailSuccessModalOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
      >
        <div className={styles.modal}>
          <div className={styles.modalIconContainer}>
            <div className={styles.modalIcon}>
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
          <h4 className={styles.modalTitle}>Email Sent Successfully!</h4>
          <p className={styles.modalMessage}>
            The quotation has been sent to the client's email.
          </p>
          <div className={styles.modalButtonContainer}>
            <button
              onClick={() => setEmailSuccessModalOpen(false)}
              className={styles.modalButton}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationView;